import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { ImageIcon, Plus } from "lucide-react";
import { HoverCard, Skeleton } from "@/components/ui";
import LibraryCard from "@/components/Cells/LibraryCard";
import { HoloBorder } from "@/components/Holo";
import { cn } from "@/lib/utils";
import { DesignPackageServices } from "@/services/bedrock/DesignPackageServices";
import { DesignPackageListParams, DesignPackageSummary } from "@/services/bedrock/dtos/DesignPackage";
import { designKeys } from "@/services/bedrock/queryKeys";
import { useDesignPackages } from "../hooks/useDesignPackages";
import { TEMPLATE_PRESETS } from "../templatePresets";
import { GLOBALS_STALE_TIME } from "./InspirationStrip";
import { ASPECT_CLASS } from "./PackageCard";

interface TemplateRowProps {
  selectedId: number | null;
  onSelect(template: DesignPackageSummary | null): void;
  disabled?: boolean;
}

export const TEMPLATE_ROW_PARAMS: DesignPackageListParams = {
  scope: "global",
  status: "published",
  perPage: 12,
  sort: "publishedAt",
  order: "desc",
};

export function useTemplateRowPackages() {
  return useDesignPackages(TEMPLATE_ROW_PARAMS, { staleTime: GLOBALS_STALE_TIME });
}

const REQUEST_PREVIEW_LENGTH = 280;
const CARD_CLASS = "w-32 shrink-0 snap-start";

// List summaries don't carry the request text, so the hover card reads it from the detail once opened.
const TemplateHoverContent: React.FC<{ template: DesignPackageSummary }> = ({ template }) => {
  const { data, isLoading } = useQuery({
    queryKey: designKeys.detail(template.id),
    queryFn: () => DesignPackageServices.get(template.id),
    staleTime: GLOBALS_STALE_TIME,
  });
  const request = data?.instructions?.trim() ?? "";
  const shown = request.length > REQUEST_PREVIEW_LENGTH ? `${request.slice(0, REQUEST_PREVIEW_LENGTH)}…` : request;

  return (
    <div className="flex flex-col gap-2 text-xs text-slate-600">
      <span className="font-bold uppercase tracking-wider text-slate-400">What was asked for</span>
      {isLoading ? <Skeleton className="h-10 w-full" /> : shown && <p className="whitespace-pre-line">{shown}</p>}
      <p>Personalized for every player who shares it.</p>
      <p className="text-slate-400">
        Start from this look builds a new design in this style; Add to my designs on the list page copies it exactly.
      </p>
    </div>
  );
};

const TemplateCard: React.FC<{
  template: DesignPackageSummary;
  selected: boolean;
  disabled?: boolean;
  onSelect(): void;
}> = ({ template, selected, disabled, onSelect }) => {
  const teaser = TEMPLATE_PRESETS[template.name]?.teaser;
  const body = (
    <div className={cn("flex flex-col gap-1.5 p-1.5", !selected && "rounded-xl border border-slate-200 bg-white")}>
      <div className={cn("w-full overflow-hidden rounded-lg bg-slate-100", ASPECT_CLASS[template.aspectRatio])}>
        {template.thumbnailUrl ? (
          <img src={template.thumbnailUrl} alt="" className="size-full object-cover" />
        ) : (
          <div className="size-full flex items-center justify-center text-slate-300">
            <ImageIcon className="size-6" aria-hidden="true" />
          </div>
        )}
      </div>
      <span className="text-xs font-semibold text-slate-800 truncate">{template.name}</span>
      {teaser && <span className="text-[11px] text-slate-500 line-clamp-1">{teaser}</span>}
    </div>
  );

  return (
    <HoverCard>
      <HoverCard.Trigger
        render={
          <button
            type="button"
            disabled={disabled}
            aria-pressed={selected}
            data-testid={`template-${template.id}`}
            onClick={onSelect}
            className={cn(CARD_CLASS, "text-left disabled:opacity-50", !disabled && "cursor-pointer")}
          />
        }
      >
        {selected ? (
          <HoloBorder active radius="xl">
            {body}
          </HoloBorder>
        ) : (
          body
        )}
      </HoverCard.Trigger>
      <HoverCard.Content side="bottom" className="w-72">
        <TemplateHoverContent template={template} />
      </HoverCard.Content>
    </HoverCard>
  );
};

export const TemplateRow: React.FC<TemplateRowProps> = ({ selectedId, onSelect, disabled }) => {
  const { data, isLoading, isError } = useTemplateRowPackages();
  const templates = isError ? [] : data?.content ?? [];

  return (
    <div className="flex gap-3 overflow-x-auto snap-x pb-2" data-testid="template-row">
      {isLoading
        ? Array.from({ length: 4 }, (_, i) => <LibraryCard key={i} dataSkeleton className="my-0" />)
        : templates.map(template => (
            <TemplateCard
              key={template.id}
              template={template}
              selected={template.id === selectedId}
              disabled={disabled}
              onSelect={() => onSelect(template)}
            />
          ))}
      <button
        type="button"
        disabled={disabled}
        onClick={() => onSelect(null)}
        className={cn(
          CARD_CLASS,
          "min-h-40 rounded-xl border-2 border-dashed border-slate-300 flex flex-col items-center justify-center gap-2 text-xs font-semibold text-slate-500",
          "hover:border-slate-400 hover:text-slate-700 disabled:opacity-50"
        )}
      >
        <Plus className="size-5" aria-hidden="true" />
        Start from scratch
      </button>
    </div>
  );
};
